import { WinCounter } from '@/managers/winning/WinCounter'
import { SymbolType } from '@/containers/symbols/SymbolType'
import { SYMBOL_KEY_TO_ID, SYMBOL_KEY_TO_MULTIPLIER } from '@/gameConfigs/symbolMap'
import { IModel } from '@/model/IModel'
import { IRoundModel } from '@/model/IRoundModel'
import { ISpinModel } from '@/model/ISpinModel'
import { CombinationsData, IInitResponse, ISpinResponse, RealityCheckData, SymbolData } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import { GameStateMachine } from '@/states/GameStateMachine'
import { GameStatesEnum } from '@/states/GameStatesEnum'
import { normalizeHoldAndWinCoinValue } from '@/utils/HoldAndWinCoinValue'

export class Model implements IModel {
    public roundState: IRoundModel
    public spinState: ISpinModel
    public winCounter: WinCounter
    public isBonusGameNow: boolean
    public realityCheckData: RealityCheckData
    public bets: number[]
    public isPremiumSpinsActive: boolean
    public winCurrency: number
    public isBuyBonusPending: boolean
    public buyBonusPickPosition: number
    public isFreeSpinsRestore: boolean
    public combinationsData: CombinationsData
    public winPositions: number[][]
    public initialSlotGrid: SymbolData[][]
    public cascadeIndex: number
    public bigWinLevel: number | null
    public bonusBuyBet: number | null
    public tournamentData: any | null

    //## One-shot guard so the capped-round popup cannot replay if FinalState re-walks its children.
    public isMaxExposureShown: boolean

    private _stateMachine: GameStateMachine
    private _currentBet: number
    private _initData: IInitResponse
    private _spinData: ISpinResponse
    private _balance: number = 0
    private _promoSessionActive = false
    /** Spins left the last automatic promo spin was started on; guards against a frozen count. */
    private _lastPromoAutoSpinSpinsLeft: number | null = null

    public constructor() {
        this.isBonusGameNow = false
        this.winCounter = new WinCounter()
        this.balance = 0
        this.tournamentData = null
        this.bets = []
        this.isBuyBonusPending = false
        this.buyBonusPickPosition = 0
    }

    public init() {
        this._stateMachine = new GameStateMachine(this)
        this._stateMachine.setState(GameStatesEnum.BEFORE_LOADING_STATE)

        this.resetRoundState()
        this.resetSpinState()
    }

    public get balance(): number {
        return this._balance
    }

    public set balance(value: number) {
        this._balance = value
    }

    public get initData(): IInitResponse {
        return this._initData
    }

    public set initData(value: IInitResponse) {
        this._initData = value

        const coinValues = value.game.coin_values
            ? String(value.game.coin_values)
                  .split(';')
                  .map((v: string) => Number(v))
                  .filter((v: number) => Number.isFinite(v) && v > 0)
            : []

        const safeCoinValueList = coinValues.length
            ? coinValues
            : [
                  0.01, 0.02, 0.03, 0.04, 0.05, 0.06, 0.07, 0.08, 0.09, 0.1, 0.12, 0.16, 0.2, 0.25, 0.3, 0.4, 0.5, 0.6,
                  0.7, 0.75, 0.8, 0.9, 1, 1.2, 1.4, 1.5, 2, 2.5, 3, 5, 10, 15, 20,
              ]

        this.balance = Number(value.player.balance ?? 0)
        this.bets = safeCoinValueList.map((bet) => bet * this.fixedCoinsBet)

        const previousRoundData: any = value.previous_round || {}
        const nextRound = Array.isArray(value.next_round) ? null : (value.next_round as any)
        const nextRoundType = String(nextRound?.type || '').toLowerCase()
        const isFsRestore =
            nextRoundType === 'freespins' ||
            nextRoundType === 'freespin'
        const isRespinRestore = nextRoundType === 'respins' || nextRoundType === 'respin'
        const hasFeatureRestore = !!nextRound && (isFsRestore || isRespinRestore)

        const previousRoundSlotGrid = this.parseMatrixToSlotGrid(previousRoundData.matrix || '', previousRoundData)
        this.initialSlotGrid = hasFeatureRestore
            ? previousRoundSlotGrid
            : this.resolveBaseGameSlotGrid(previousRoundData, previousRoundSlotGrid)

        if (!this.initialSlotGrid.length) {
            this.initialSlotGrid = this.createRandomInitialSlotGrid()
        }

        this.resetCurrentBet()

        const previousCoinValue = Number(value.previous_round?.coin_value)
        if (Number.isFinite(previousCoinValue) && previousCoinValue > 0) {
            this.currentBet = previousCoinValue * this.fixedCoinsBet
        }

        if (isFsRestore && nextRound) {
            const holdAndWinExtra = this.resolveHoldAndWinExtra(previousRoundData)
            const previousRoundWin = Number(value.previous_round?.win_amount ?? 0)
            const totalSpins = Number(nextRound.num_spins)
            const spinsLeft = Number(nextRound.spins_left)
            const totalFsWin = Number(nextRound.total_fs_win_amount ?? 0)

            this.spinData = {
                balance: this.balance,
                base_win_currency: Number.isFinite(previousRoundWin) ? previousRoundWin : 0,
                bonus: {
                    bonus_amount: 0,
                    bonus_win_currency: 0,
                    freebet_count: 0,
                    freebet_given: 0,
                    freebets_position: [[]],
                    jackpot_won: [],
                    multipliers: [],
                    scatter_count: 0,
                    scatters_position: [],
                    num_coin_symbols: holdAndWinExtra.num_coin_symbols,
                },
                currency: value.player.currency,
                amount_type: String(nextRound.amount_type ?? previousRoundData?.amount_type ?? ''),
                promo_details: value.promo_details || null,
                freebet: {
                    amount_left: Number.isFinite(spinsLeft) ? Math.max(0, spinsLeft) : 0,
                    coin_amount: this.fixedCoinsBet,
                    coin_value: Number(nextRound.coin_value ?? value.previous_round.coin_value ?? this.currentCoinBet),
                    logic: { level: 0, lines_played: 0 },
                    total_amount_given: Number.isFinite(totalSpins) ? Math.max(0, totalSpins) : 0,
                    total_coin_win: Number.isFinite(totalFsWin) ? totalFsWin : 0,
                    total_win: Number.isFinite(totalFsWin) ? totalFsWin : 0,
                    triggered_win_amount: 0,
                    triggered_win_amount_currency: 0,
                    triggered_win_bonus_amount: 0,
                    triggered_win_bonus_amount_currency: 0,
                },
                freeround: null,
                spin_type: String(previousRoundData?.spin_type || 'normal').toLowerCase(),
                next_round_type: 'freespins',
                hold_and_win_extra: holdAndWinExtra,
                session_token: '',
                slot_grid: previousRoundSlotGrid.length ? [previousRoundSlotGrid] : [],
                status: 0,
                win_coin: Number.isFinite(previousRoundWin) ? previousRoundWin : 0,
                win_currency: Number.isFinite(previousRoundWin) ? previousRoundWin : 0,
                winning_combinations: [[]],
            }
        }

        if (isRespinRestore && nextRound) {
            const previousRoundWin = Number(previousRoundData?.win_amount ?? 0)
            const totalRespins = Number(nextRound.num_spins)
            const respinsLeft = Number(nextRound.spins_left)
            const resolvedRespinsLeft = Number.isFinite(respinsLeft) ? Math.max(0, respinsLeft) : 0
            const resolvedRespinsTotal = Number.isFinite(totalRespins)
                ? Math.max(0, totalRespins)
                : resolvedRespinsLeft
            const holdAndWinExtra = this.resolveHoldAndWinExtra(previousRoundData)
            const nestedFreeSpinsParentType = this.resolveNestedFreeSpinsParentType(previousRoundData, nextRound)
            const nestedFreebet = nestedFreeSpinsParentType
                ? this.resolveNestedFreeSpinsFreebet(previousRoundData, nextRound)
                : null
            const totalFsWin = Number(nextRound?.total_fs_win_amount ?? previousRoundData?.total_fs_win_amount ?? 0)

            this.spinData = {
                balance: this.balance,
                base_win_currency: Number.isFinite(previousRoundWin) ? previousRoundWin : 0,
                bonus: {
                    bonus_amount: 0,
                    bonus_win_currency: 0,
                    freebet_count: 0,
                    freebet_given: 0,
                    freebets_position: [[]],
                    jackpot_won: [],
                    multipliers: [],
                    scatter_count: 0,
                    scatters_position: [],
                    num_coin_symbols: holdAndWinExtra.num_coin_symbols,
                },
                currency: value.player.currency,
                freebet: nestedFreebet,
                freeround: null,
                spin_type: 'respin',
                next_round_type: 'respins',
                respins_left: resolvedRespinsLeft,
                respins_total: resolvedRespinsTotal,
                hold_and_win_extra: holdAndWinExtra,
                session_token: '',
                slot_grid: previousRoundSlotGrid.length ? [previousRoundSlotGrid] : [],
                status: 0,
                win_coin: Number.isFinite(previousRoundWin) ? previousRoundWin : 0,
                win_currency: Number.isFinite(previousRoundWin) ? previousRoundWin : 0,
                winning_combinations: [[]],
                parent_type: nestedFreeSpinsParentType,
                current_round: previousRoundData,
                next_round: nextRound,
                total_fs_win_amount: Number.isFinite(totalFsWin) ? totalFsWin : 0,
                total_fs_win_amount_currency: Number.isFinite(totalFsWin) ? totalFsWin : 0,
            } as ISpinResponse
        }

        this.realityCheckData = {
            startSessionTime: Date.now(),
            totalSessionBet: 0,
            totalSessionWin: 0,
        }
    }

    public get spinData(): ISpinResponse {
        return this._spinData
    }

    public set spinData(value: ISpinResponse) {
        this._spinData = value
        // 0 is a valid post-purchase balance (e.g. Buy Bonus spends the entire wallet).
        const nextBalance = Number(value?.balance)
        if (Number.isFinite(nextBalance) && nextBalance >= 0) {
            this.balance = nextBalance
        }
        this.winCurrency = value.win_currency
    }

    public get currentBet(): number {
        return this._currentBet
    }

    public set currentBet(value: number) {
        if (this._promoSessionActive || !this.maxBet || value <= this.maxBet) {
            this._currentBet = value
            GameSignals.betHasChanged.emit(this._currentBet, this.currentBaseBet)
        }
    }

    public resetCurrentBet() {
        this.currentBet = this.defaultCoinValue * this.fixedCoinsBet
    }

    get currentBaseBet(): number {
        return this._currentBet
    }

    public get currentCoinBet(): number {
        return +(this._currentBet / this.fixedCoinsBet).toFixed(2)
    }

    public get currentSpinBet(): number {
        return this.currentBet
    }

    get fixedCoinsBet(): number {
        return Number(this._initData?.game?.num_coins || this._initData?.game?.fixed_coins || 20)
    }

    get defaultCoinValue(): number {
        return Number(this._initData?.game?.default_coin_value || 1)
    }

    get maxBet(): number {
        return Number(this._initData?.game?.gamble_max_bet || 0)
    }

    get numColumns(): number {
        return Number(this._initData?.game?.num_columns || 6)
    }

    get numPaylines(): number {
        return Number(this._initData?.game?.num_paylines || 1)
    }

    public freeRoundsActive(): boolean {
        return !!this.spinData?.freeround?.is_active
    }

    public isPromoSpinsRound(): boolean {
        return this._promoSessionActive
    }

    public hasPromoSpinsRemaining(): boolean {
        if (!this._promoSessionActive) {
            return false
        }

        const promoDetails = (this._spinData as any)?.promo_details
        if (Number(promoDetails?.pfs_end_state) === 1 || Number(promoDetails?.max_win_reached) === 1) {
            return false
        }

        const spinsLeft = this.resolvePromoSpinsLeft()
        return Number.isFinite(spinsLeft) && spinsLeft > 0
    }

    private resolvePromoSpinsLeft(): number {
        const promoDetails = (this._spinData as any)?.promo_details
        return Number(promoDetails?.spins_left ?? this._spinData?.freebet?.amount_left)
    }

    /**
     * Whether the game may start the next promo spin on its own.
     *
     * Beyond having spins left, the count has to have moved since the last one it started. A promo
     * cancelled on the server simply stops being described in the spin responses, and the parser
     * then carries the last block forward untouched — so the count freezes above zero and the idle
     * state keeps firing spins at the player's own stake, with no way out short of a reload.
     */
    public canAutoStartPromoSpin(): boolean {
        if (!this.hasPromoSpinsRemaining()) {
            return false
        }

        return this._lastPromoAutoSpinSpinsLeft !== this.resolvePromoSpinsLeft()
    }

    public markPromoAutoSpinStarted(): void {
        this._lastPromoAutoSpinSpinsLeft = this.resolvePromoSpinsLeft()
    }

    public beginPromoSession(): void {
        this._promoSessionActive = true
        this._lastPromoAutoSpinSpinsLeft = null
    }

    public endPromoSession(applyBalance?: number): void {
        this._promoSessionActive = false
        this._lastPromoAutoSpinSpinsLeft = null
        if (Number.isFinite(applyBalance) && (applyBalance as number) >= 0) {
            this.balance = applyBalance as number
        }
    }

    public resetRoundState() {
        this.roundState = {
            isRestoredRound: false,
            isFreeSpins: false,
        }
    }

    public resetSpinState() {
        this.spinState = {
            spinStartTime: Date.now(),
            bonusGridIndex: 0,
            bonusGridsCount: 0,
            isHoldAndWinActive: false,
            needsBetCooldown: true,
            isIdleStateWinSkipped: false,
            isWinSkipped: false,
        }

        this.cascadeIndex = 0
        this.bigWinLevel = null
        this.isMaxExposureShown = false
    }

    private static readonly NON_COIN_SYMBOL_KEYS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'w']

    private createRandomInitialSlotGrid(): SymbolData[][] {
        const rowCount = Math.max(1, Number(this._initData?.game?.num_rows || 3))
        const colCount = Math.max(1, Number(this._initData?.game?.num_columns || 5))
        const rows: string[] = []

        for (let row = 0; row < rowCount; row++) {
            let rowSymbols = ''

            for (let col = 0; col < colCount; col++) {
                const keys = Model.NON_COIN_SYMBOL_KEYS
                rowSymbols += keys[Math.floor(Math.random() * keys.length)]
            }

            rows.push(rowSymbols)
        }

        return this.parseMatrixToSlotGrid(rows.join(';'))
    }

    /**
     * Grid the base game is loaded with once no feature is left to resume.
     *
     * `previous_round.matrix` is the last round played, which after a Hold & Win is the respin
     * board: painting it on the base reels leaves the feature coins stuck there. The server ships
     * the base game screen that triggered the feature in `other_feature_win_details.base_game_info`,
     * so that one wins whenever it is sent.
     */
    private resolveBaseGameSlotGrid(previousRoundData: any, previousRoundSlotGrid: SymbolData[][]): SymbolData[][] {
        const baseGameInfo = this.resolveBaseGameInfo(previousRoundData)

        if (!baseGameInfo) {
            return previousRoundSlotGrid
        }

        const baseGameGrid = this.parseMatrixToSlotGrid(String(baseGameInfo.matrix || ''), {
            coin_value: previousRoundData?.coin_value,
            extra: baseGameInfo,
        })

        return baseGameGrid.length ? baseGameGrid : previousRoundSlotGrid
    }

    private resolveBaseGameInfo(roundData?: any): any | null {
        const baseGameInfo = this.parseMaybeSerializedObject(this.resolveRoundExtra(roundData)?.base_game_info)

        if (!baseGameInfo || !String(baseGameInfo.matrix || '').trim()) {
            return null
        }

        return baseGameInfo
    }

    private parseMatrixToSlotGrid(matrix: string, roundData?: any): SymbolData[][] {
        const rows = matrix
            .split(';')
            .map((row) => row.trim())
            .filter((row) => row.length > 0)

        if (!rows.length) return []

        const rowCount = rows.length
        const colCount = rows[0].length
        const reels: SymbolData[][] = []
        const resolvedSpinCoinValue = Number(
            roundData?.coin_value ?? this._spinData?.freebet?.coin_value ?? this.currentCoinBet ?? this.defaultCoinValue
        )

        // Build coin metadata map from other_feature_win_details or extra
        const extra = this.resolveRoundExtra(roundData)
        const coinIndexes: number[] = Array.isArray(extra?.coin_index) ? extra.coin_index : []
        const coinValues: any[] = Array.isArray(extra?.coin_values) ? extra.coin_values : []
        const coinTypes: any[] = Array.isArray(extra?.coin_types) ? extra.coin_types : []
        const coinMetaByFlatIndex = new Map<number, { coinValue?: number; coinType?: string }>()
        coinIndexes.forEach((rawIndex: any, idx: number) => {
            const flatIndex = Number(rawIndex)
            if (!Number.isFinite(flatIndex) || flatIndex < 0) return
            const coinType = String(coinTypes[idx] || '').trim().toLowerCase() || undefined
            const coinValue = normalizeHoldAndWinCoinValue(coinValues[idx], coinType, resolvedSpinCoinValue)
            coinMetaByFlatIndex.set(flatIndex, { ...(coinValue ? { coinValue } : {}), ...(coinType ? { coinType } : {}) })
        })

        for (let col = 0; col < colCount; col++) {
            reels[col] = []
            for (let row = 0; row < rowCount; row++) {
                let symbolKey = String(rows[row][col] || '').toLowerCase()
                // 'x' in previous_round matrix means an empty/invalid cell: replace with random non-coin symbol
                if (symbolKey === 'x') {
                    const keys = Model.NON_COIN_SYMBOL_KEYS
                    symbolKey = keys[Math.floor(Math.random() * keys.length)]
                }
                const flatIndex = row * colCount + col
                const coinMeta = coinMetaByFlatIndex.get(flatIndex)
                const fallbackMultiplier = SYMBOL_KEY_TO_MULTIPLIER[symbolKey] ?? null
                const multiplier = coinMeta?.coinValue ?? fallbackMultiplier
                reels[col].push({
                    id: SYMBOL_KEY_TO_ID[symbolKey] ?? SymbolType.LP_1,
                    symbolKey,
                    ...(Number.isFinite(resolvedSpinCoinValue) && resolvedSpinCoinValue > 0
                        ? { spinCoinValue: resolvedSpinCoinValue }
                        : {}),
                    ...(multiplier ? { multiplier } : {}),
                    ...(coinMeta?.coinType ? { coinType: coinMeta.coinType } : {}),
                    ...(Number.isFinite(coinMeta?.coinValue as number) && (coinMeta?.coinValue as number) > 0 ? { coinValue: coinMeta.coinValue } : {}),
                })
            }
        }

        return reels
    }

    private isFreeSpinsParentType(value: unknown): boolean {
        const type = String(value || '').toLowerCase()
        return type === 'freespins' || type === 'freespin'
    }

    private resolveNestedFreeSpinsParentType(previousRoundData: any, nextRound: any): string {
        const bonusParentTypes = Array.isArray(previousRoundData?.bonus_games_won)
            ? previousRoundData.bonus_games_won.map((bonus: any) => bonus?.parent_type)
            : []
        const candidates = [
            previousRoundData?.parent_type,
            nextRound?.parent_type,
            previousRoundData?.spin_type,
            ...bonusParentTypes,
        ]

        return candidates.find((candidate) => this.isFreeSpinsParentType(candidate)) ? 'freespins' : ''
    }

    private resolveNestedFreeSpinsFreebet(previousRoundData: any, nextRound: any): ISpinResponse['freebet'] {
        const previousSpinType = String(previousRoundData?.spin_type || '').toLowerCase()
        const previousLooksLikeFreeSpin = this.isFreeSpinsParentType(previousSpinType)
        const spinsLeft = previousLooksLikeFreeSpin ? Number(previousRoundData?.spins_left) : NaN
        const totalSpins = previousLooksLikeFreeSpin ? Number(previousRoundData?.num_spins) : NaN
        const totalFsWin = Number(nextRound?.total_fs_win_amount ?? previousRoundData?.total_fs_win_amount ?? 0)

        return {
            amount_left: Number.isFinite(spinsLeft) ? Math.max(0, spinsLeft) : 1,
            coin_amount: this.fixedCoinsBet,
            coin_value: Number(nextRound?.coin_value ?? previousRoundData?.coin_value ?? this.currentCoinBet),
            logic: { level: 0, lines_played: 0 },
            total_amount_given: Number.isFinite(totalSpins) ? Math.max(0, totalSpins) : 1,
            total_coin_win: Number.isFinite(totalFsWin) ? totalFsWin : 0,
            total_win: Number.isFinite(totalFsWin) ? totalFsWin : 0,
            triggered_win_amount: 0,
            triggered_win_amount_currency: 0,
            triggered_win_bonus_amount: 0,
            triggered_win_bonus_amount_currency: 0,
        }
    }

    private resolveHoldAndWinExtra(roundData?: any): {
        coin_index: number[]
        coin_values: number[]
        coin_types: string[]
        num_coin_symbols: number
        ladder_count: number
        ladder_unlock_milestones_count: number[]
    } {
        const extra = this.resolveRoundExtra(roundData)
        const bonusGamesWon: any[] = Array.isArray(roundData?.bonus_games_won) ? roundData.bonus_games_won : []
        const freeSpinsBonus = bonusGamesWon.find((bonus: any) => {
            const type = String(bonus?.type || '').toLowerCase()
            return type === 'freespins' || type === 'freespin'
        })
        const ladderInfo = extra?.ladder_info && typeof extra.ladder_info === 'object' ? extra.ladder_info : null

        const coinIndex: number[] = Array.isArray(extra?.coin_index)
            ? extra.coin_index.map((value: any) => Number(value)).filter((value: number) => Number.isFinite(value))
            : []
        const coinTypes: string[] = Array.isArray(extra?.coin_types)
            ? extra.coin_types.map((value: any) => String(value || '').trim().toLowerCase())
            : []
        const resolvedSpinCoinValue = Number(
            roundData?.coin_value ?? this._spinData?.freebet?.coin_value ?? this.currentCoinBet ?? this.defaultCoinValue
        )
        const coinValues: number[] = Array.isArray(extra?.coin_values)
            ? extra.coin_values.map((value: any, index: number) => {
                const normalized = normalizeHoldAndWinCoinValue(value, coinTypes[index], resolvedSpinCoinValue)
                return Number.isFinite(normalized) ? Number(normalized) : 0
              })
            : []
        const ladderUnlockMilestones: number[] = Array.isArray(
            ladderInfo?.unlock_milestones_count ?? freeSpinsBonus?.unlock_milestones_count
        )
            ? (ladderInfo?.unlock_milestones_count ?? freeSpinsBonus?.unlock_milestones_count)
                  .map((value: any) => Number(value))
                  .filter((value: number) => Number.isFinite(value))
            : []

        return {
            coin_index: coinIndex,
            coin_values: coinValues,
            coin_types: coinTypes,
            num_coin_symbols: Number.isFinite(Number(extra?.num_coin_symbols))
                ? Number(extra.num_coin_symbols)
                : coinIndex.length,
            ladder_count: Number(ladderInfo?.ladder_count ?? freeSpinsBonus?.ladder_count ?? 0),
            ladder_unlock_milestones_count: ladderUnlockMilestones,
        }
    }

    private resolveRoundExtra(roundData?: any): any {
        return (
            this.parseMaybeSerializedObject(roundData?.other_feature_win_details) ??
            this.parseMaybeSerializedObject(roundData?.extra) ??
            {}
        )
    }

    private parseMaybeSerializedObject(value: any): any | null {
        if (typeof value === 'string' && value.trim().length > 0) {
            try {
                const parsed = JSON.parse(value)
                return parsed && typeof parsed === 'object' ? parsed : null
            } catch {
                // Keep legacy fallback when backend sends malformed JSON.
                return null
            }
        }

        return value && typeof value === 'object' && !Array.isArray(value) ? value : null
    }
}
